import api from './api';

// ===================================================================
// LiveChatService — generischer Gruppen-Live-Chat pro Entität
// (2026-08-27, zuerst für Tippspiele). Backend:
// dartcloud-backend/src/core/routes/live-chat-routes.js
// Realtime: Socket.io-Channel `live-chat:<ENTITY_TYPE>:<entityId>`,
// Events `liveChatMessage` / `liveChatMessageDeleted`.
// ===================================================================

/** URL-Segmente, die das Backend kennt (neuer Bereich = eine Zeile) */
export type LiveChatEntitySegment =
  | 'prediction-game'
  | 'tournament'
  | 'club'
  | 'pub'
  | 'shop'
  | 'series'
  | 'league'
  | 'association'
  | 'lobby';

const SEGMENT_TO_ENUM: Record<LiveChatEntitySegment, string> = {
  'prediction-game': 'PREDICTION_GAME',
  tournament: 'TOURNAMENT',
  club: 'CLUB',
  pub: 'PUB',
  shop: 'SHOP',
  series: 'SERIES',
  league: 'LEAGUE',
  association: 'ASSOCIATION',
  // Globaler Community-Chat der Startseite: entityId ist immer 'global'
  lobby: 'LOBBY',
};

/** Feste entityId des globalen Lobby-Chats (Kanal `live-chat:LOBBY:global`) */
export const LOBBY_GLOBAL_ID = 'global';

export interface LiveChatMessage {
  id?: string | null;
  entityType?: string | null;
  entityId?: string | null;
  senderId?: string | null;
  senderName?: string | null;
  senderAvatarUrl?: string | null;
  content?: string | null;
  createdAt?: number | null;
  /** Likes (2026-09-03, additiv): fehlt bei alten Antworten → 0 / false */
  likeCount?: number;
  likedByMe?: boolean;
  [key: string]: any;
}

export interface LiveChatLikeResult {
  id: string;
  liked: boolean;
  likeCount: number;
}

/** Socket-Payload `liveChatMessageLiked` */
export interface LiveChatLikeEvent {
  id?: string | null;
  entityType?: string | null;
  entityId?: string | null;
  likeCount?: number;
  userId?: string | null;
  liked?: boolean;
}

export interface LiveChatMessagesResult {
  messages: LiveChatMessage[];
  hasMore: boolean;
}

function parseMessage(data: any): LiveChatMessage | null {
  if (!data || typeof data !== 'object' || !data.id) return null;
  return {
    id: data.id ?? null,
    entityType: data.entityType ?? null,
    entityId: data.entityId ?? null,
    senderId: data.senderId ?? null,
    senderName: data.senderName ?? null,
    senderAvatarUrl: data.senderAvatarUrl ?? null,
    content: data.content ?? null,
    createdAt: typeof data.createdAt === 'number' ? data.createdAt : null,
    likeCount: typeof data.likeCount === 'number' && data.likeCount >= 0 ? data.likeCount : 0,
    likedByMe: data.likedByMe === true,
  };
}

const liveChatService = {
  /** Socket.io-Channel eines Chat-Raums (muss exakt zum Backend passen) */
  buildChannelName(segment: LiveChatEntitySegment, entityId: string): string {
    return `live-chat:${SEGMENT_TO_ENUM[segment]}:${entityId}`;
  },

  /** Nachrichten laden (chronologisch aufsteigend); `before` lädt ältere nach */
  async getMessages(
    segment: LiveChatEntitySegment,
    entityId: string,
    options: { limit?: number; before?: number | null } = {},
  ): Promise<LiveChatMessagesResult> {
    const params: Record<string, any> = {};
    if (options.limit) params.limit = options.limit;
    if (options.before != null) params.before = options.before;
    const res = await api.get(`/api/live-chat/${segment}/${entityId}/messages`, { params });
    const raw = Array.isArray(res.data?.messages) ? res.data.messages : [];
    return {
      messages: raw.map(parseMessage).filter(Boolean) as LiveChatMessage[],
      hasMore: res.data?.hasMore === true,
    };
  },

  /** Nachricht senden (nur angemeldet) */
  async postMessage(
    segment: LiveChatEntitySegment,
    entityId: string,
    content: string,
  ): Promise<LiveChatMessage | null> {
    const res = await api.post(`/api/live-chat/${segment}/${entityId}/messages`, { content });
    return parseMessage(res.data);
  },

  /** Nachricht löschen (Absender oder DC-Admin) */
  async deleteMessage(messageId: string): Promise<void> {
    await api.post(`/api/live-chat/message/${messageId}/delete`);
  },

  /** Like einer Nachricht togglen (nur angemeldet) → { id, liked, likeCount } */
  async toggleLike(messageId: string): Promise<LiveChatLikeResult> {
    const res = await api.post(`/api/live-chat/message/${encodeURIComponent(messageId)}/like`);
    const data = res?.data ?? {};
    return {
      id: typeof data.id === 'string' ? data.id : messageId,
      liked: data.liked === true,
      likeCount: typeof data.likeCount === 'number' && data.likeCount >= 0 ? data.likeCount : 0,
    };
  },
};

export default liveChatService;
